/**
 * @file: 弹窗+iframe
 * @author: v_shiying03
 */

import React, {useEffect, FC, useState, useRef, useCallback} from 'react';
import {Modal} from '@baidu/qianfan-antd-kit';
import type {ModalProps} from '@baidu/qianfan-antd-kit';
import {Image} from 'antd';
import CloseIcon from '@/styles/assets/icons/close.svg?react';
import {HOST_CONFIG} from '@/modules/Qualify/common/constant';
import styles from './index.less';

export type ENV_TYPE = 'dev' | 'sandbox' | 'online';
export type InitialAuthType = 'enterprise' | 'personal';

export type SdkModalProps = Omit<
    ModalProps,
    'open' | 'destroyOnClose' | 'width' | 'onCancel' | 'footer'
    | 'styles' | 'centered' | 'className' | 'closeIcon' | 'modalRender'
>;

export interface QualifyModalSDKProps {
    visible: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
    iframeStyle?: React.CSSProperties;
    env?: ENV_TYPE;
    initialAuthType?: InitialAuthType;
    modalProps?: SdkModalProps;
}

const SAFETY_MARGIN = 80; // modal与页面安全边距
const DEFAULT_HEIGHT = 500;
const DEFAULT_AUTH_PATH = '/qualify/index';
const AUTH_PATH_MAP: Record<InitialAuthType, string> = {
    enterprise: '/qualify/company/legal',
    personal: '/qualify/person/bank',
};

function getBaseUrl(env: ENV_TYPE): string {
    switch (env) {
        case 'dev':
            return HOST_CONFIG.localhost;
        case 'sandbox':
            return HOST_CONFIG.sandbox;
        case 'online':
        default:
            return HOST_CONFIG.online;
    }
}

function getInitialAuthPath(initialAuthType?: InitialAuthType): string {
    return initialAuthType ? AUTH_PATH_MAP[initialAuthType] || DEFAULT_AUTH_PATH : DEFAULT_AUTH_PATH;
}

interface PreviewConfigProps {
    visible: boolean;
    src: string;
}

const IframeModal: FC<QualifyModalSDKProps> = ({
    visible,
    env = 'online',
    initialAuthType,
    modalProps,
    onClose,
    onSuccess,
}) => {
    const [iframeHeight, setIframeHeight] = useState(DEFAULT_HEIGHT);
    const [previewConfig, setPreviewConfig] = useState<PreviewConfigProps>({
        visible: false,
        src: '',
    });
    const [iframeLoaded, setIframeLoaded] = useState(false);
    // 缓存 iframe 最新上报的内容高度
    const latestContentHeightRef = useRef<number>(DEFAULT_HEIGHT);

    const handlePreviewClose = (visible: boolean) => {
        if (!visible) {
            setPreviewConfig(prev => ({...prev, visible: false}));
        }
    };

    /** 统一计算最终展示高度 */
    const computeTargetHeight = useCallback((contentHeight: number) => {
        let targetHeight = DEFAULT_HEIGHT;
        if (contentHeight > DEFAULT_HEIGHT) {
            const maxHeight = window.innerHeight - SAFETY_MARGIN * 2;
            targetHeight = Math.min(contentHeight, maxHeight);
        }
        return Math.max(targetHeight, DEFAULT_HEIGHT);
    }, []);

    // 监听iframe高度变化与窗口大小变化
    useEffect(() => {
        if (!visible) {
            return;
        }

        // eslint-disable-next-line complexity
        const handleIframeMessage = (event: MessageEvent) => {
            const allowedOrigins = Object.values(HOST_CONFIG);
            const {data, origin} = event;

            if (!data || !origin || !allowedOrigins.includes(origin)) {
                return;
            }

            if (data?.type === 'PREVIEW_IMAGE' && data?.src) {
                const {src} = data;
                if (typeof src === 'string' && (src.startsWith('http') || src.startsWith('/'))) {
                    setPreviewConfig({visible: true, src});
                }
            }

            // 监听来自iframe的高度消息
            if (data?.type === 'QUALIFY_HEIGHT') {
                const contentHeight = data.height;
                if (contentHeight && typeof contentHeight === 'number') {
                    latestContentHeightRef.current = contentHeight;

                    setIframeHeight(prevHeight => {
                        const targetHeight = computeTargetHeight(contentHeight);
                        if (prevHeight !== targetHeight) {
                            return targetHeight;
                        }
                        return prevHeight;
                    });
                }
            }

            // 原实名成功回调
            if (data?.qualifySuccess) {
                onSuccess?.();
            }
        };

        // 窗口resize时根据最新iframe内容高度重新计算
        const handleResize = () => {
            setIframeHeight(prevHeight => {
                const targetHeight = computeTargetHeight(latestContentHeightRef.current);
                if (prevHeight !== targetHeight) {
                    return targetHeight;
                }
                return prevHeight;
            });
        };

        // 监听来自iframe的消息
        window.addEventListener('message', handleIframeMessage);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('message', handleIframeMessage);
            window.removeEventListener('resize', handleResize);
        };
    }, [computeTargetHeight, onClose, onSuccess, visible]);

    return (
        <Modal
            centered
            destroyOnClose
            width={800}
            open={visible}
            onCancel={onClose}
            footer={null}
            closeIcon={<CloseIcon />}
            styles={{
                content: {
                    padding: 0,
                    borderRadius: 10,
                    overflow: 'hidden',
                    backgroundColor: iframeLoaded ? 'transparent' : undefined,
                },
            }}
            modalRender={modal => (
                // 弹窗的外层容器，安全距离 80px
                <div style={{padding: '80px 0'}}>{modal}</div>
            )}
            className={styles.iframeModal}
            {...modalProps}
        >
            <iframe
                src={`${getBaseUrl(env)}/qualify/#${getInitialAuthPath(initialAuthType)}`}
                width={800}
                height={iframeHeight}
                className={styles.iframeContent}
                onLoad={() => setIframeLoaded(true)}
            />
            {/* 全屏预览层：放在父页面 body 层级 */}
            {previewConfig.visible && (
                <Image
                    src={previewConfig.src}
                    preview={{
                        visible: previewConfig.visible,
                        onVisibleChange: handlePreviewClose,
                    }}
                    style={{display: 'none'}}
                />
            )}
        </Modal>
    );
};

export default IframeModal;
